export type ProfessionalCountInput = {
  totalProfessionals?: string | number | null;
  professionalsInPrimary?: string | number | null;
};

export type FirmProfessionalCountFields = {
  totalProfessionals?: number;
  firmProfessionalsInPrimary?: string;
  /** Mirror string of `totalProfessionals` for legacy consumers. */
  firmProfessionalsWorldWide?: string;
};

export type OfficeProfessionalCountFields = {
  professionalsInPrimary?: number;
  firmProfessionalsInPrimary?: string;
  /** Defaults to primary when not provided separately. */
  professionalsInWsgJurisdiction?: number;
  firmProfessionalsInWsgJurisdiction?: string;
};

type OfficeLike = {
  id?: string;
  officeType?: string | null;
};

export function parseOptionalProfessionalCount(
  value: string | number | null | undefined,
): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return undefined;
  }

  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }
  return parsed;
}

export function isValidProfessionalCountInput(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  return /^\d+$/.test(trimmed);
}

export function countToString(count: number | undefined): string | undefined {
  if (count === undefined) {
    return undefined;
  }
  return String(count);
}

export function toFirmProfessionalCountFields(
  input: ProfessionalCountInput,
): FirmProfessionalCountFields {
  const totalProfessionals = parseOptionalProfessionalCount(
    input.totalProfessionals,
  );
  const professionalsInPrimary = parseOptionalProfessionalCount(
    input.professionalsInPrimary,
  );

  const fields: FirmProfessionalCountFields = {};

  if (totalProfessionals !== undefined) {
    fields.totalProfessionals = totalProfessionals;
    fields.firmProfessionalsWorldWide = String(totalProfessionals);
  }

  if (professionalsInPrimary !== undefined) {
    fields.firmProfessionalsInPrimary = String(professionalsInPrimary);
  }

  return fields;
}

export function toOfficeProfessionalCountFields(
  professionalsInPrimary: number | undefined,
  professionalsInWsgJurisdiction?: number | undefined,
): OfficeProfessionalCountFields {
  if (professionalsInPrimary === undefined) {
    return {};
  }

  const label = String(professionalsInPrimary);
  const wsg = professionalsInWsgJurisdiction ?? professionalsInPrimary;
  return {
    professionalsInPrimary,
    firmProfessionalsInPrimary: label,
    professionalsInWsgJurisdiction: wsg,
    firmProfessionalsInWsgJurisdiction: String(wsg),
  };
}

export function findMainOffice<T extends OfficeLike>(
  offices: T[] | null | undefined,
): T | undefined {
  if (!offices?.length) {
    return undefined;
  }
  return offices.find(
    (office) => String(office.officeType ?? "").toLowerCase() === "main",
  );
}

export function resolveFirmTotalProfessionals(
  firm?: {
    totalProfessionals?: number | null;
    firmProfessionalsWorldWide?: string | null;
  } | null,
): string {
  if (firm?.totalProfessionals != null && firm.totalProfessionals >= 0) {
    return String(firm.totalProfessionals);
  }
  const legacy = String(firm?.firmProfessionalsWorldWide ?? "").trim();
  return legacy || "0";
}

export function resolveFirmPrimaryProfessionals(
  firm?: {
    firmProfessionalsInPrimary?: string | null;
    professionalsInPrimary?: number | null;
  } | null,
  mainOffice?: {
    professionalsInPrimary?: number | null;
    firmProfessionalsInPrimary?: string | null;
  } | null,
): string {
  const fromOfficePrimary = parseOptionalProfessionalCount(
    mainOffice?.professionalsInPrimary ??
      mainOffice?.firmProfessionalsInPrimary,
  );
  if (fromOfficePrimary !== undefined) {
    return String(fromOfficePrimary);
  }

  const fromFirmString = String(firm?.firmProfessionalsInPrimary ?? "").trim();
  if (fromFirmString) {
    return fromFirmString;
  }

  const fromFirmNumber = parseOptionalProfessionalCount(
    firm?.professionalsInPrimary,
  );
  if (fromFirmNumber !== undefined) {
    return String(fromFirmNumber);
  }

  return "0";
}
